import { useId, type ReactNode } from "react";

/**
 * Lo que va en la foto del frente del abanico (`AbanicoDeFotos`) cuando la tarjeta de alta
 * no sube una foto cualquiera sino una persona o una pose: así "Elegí qué agregar" se lee
 * por el dibujo, no sólo por el texto
 * (ADR-2026-10-09-las-altas-de-modelos-y-poses-se-reconocen-por-el-dibujo).
 *
 * La foto queda blanca, como la del abanico de siempre, y las figuras van todas con el
 * MISMO degradé celeste: lo único que distingue "Crear modelo" de "Subir foto" son las
 * chispas. viewBox de 50×64 (la proporción de la foto); lo de abajo a la derecha queda
 * libre porque ahí va el "+".
 */

// Del primario: con marca blanca las chispas y el degradé siguen al color del cliente.
const CHISPA = { fill: "var(--brand-primary-claro)" };

/** El lienzo: foto blanca y el degradé de las figuras. El id del degradé tiene que ser
 *  único (hay una tarjeta por pestaña y los `<defs>` son globales del documento). */
const Lienzo = ({ children }: { children: (relleno: string) => ReactNode }) => {
  const id = useId();
  return (
    <svg viewBox="0 0 50 64" className="h-full w-full">
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="64" gradientUnits="userSpaceOnUse">
          <stop offset="0" style={{ stopColor: "color-mix(in srgb, var(--brand-primary) 30%, white)" }} />
          <stop offset="1" style={{ stopColor: "color-mix(in srgb, var(--brand-primary) 45%, white)" }} />
        </linearGradient>
      </defs>
      <rect width="50" height="64" fill="#fff" />
      {children(`url(#${id})`)}
    </svg>
  );
};

/** Cabeza y hombros, como una foto carnet. */
const Busto = ({ relleno }: { relleno: string }) => (
  <>
    <circle cx="25" cy="28" r="9.5" fill={relleno} />
    <path d="M6 64c0-13 8.5-21 19-21s19 8 19 21z" fill={relleno} />
  </>
);

/** Subir la foto de una persona real: el retrato solo. */
export const RetratoDePersona = () => <Lienzo>{(relleno) => <Busto relleno={relleno} />}</Lienzo>;

/** Una persona que inventa la IA: el mismo retrato con dos chispas. */
export const RetratoCreadoConIA = () => (
  <Lienzo>
    {(relleno) => (
      <>
        <Busto relleno={relleno} />
        <path d="M39 5l1.9 4.6 4.6 1.9-4.6 1.9L39 18l-1.9-4.6-4.6-1.9 4.6-1.9z" style={CHISPA} />
        <path d="M10.5 9l1.1 2.6 2.6 1.1-2.6 1.1-1.1 2.6-1.1-2.6-2.6-1.1 2.6-1.1z" style={CHISPA} />
      </>
    )}
  </Lienzo>
);

/**
 * Una figura de cuerpo entero en pose de modelo: mano en la cintura, el peso en una pierna
 * y la otra apenas flexionada. Es lo que la pose guarda de la foto: la postura, no la
 * persona. Corrida a la izquierda para que el "+" no le tape las piernas.
 */
export const FiguraEnPose = () => (
  <Lienzo>
    {(relleno) => (
      <>
        <g stroke={relleno} strokeWidth="4.6" strokeLinecap="round" strokeLinejoin="round" fill="none">
          <path d="M19.5 20l-5 6.5 5.5 5" />
          <path d="M28.5 20l3 8 1 8" />
          <path d="M22 33.5l-1 11.5-.5 12" />
          <path d="M26 33.5l3.5 10.5-1 12.5" />
        </g>
        <path d="M19 18.5h10l-1.8 15.5h-6.4z" fill={relleno} stroke={relleno} strokeWidth="2" strokeLinejoin="round" />
        <circle cx="24" cy="9.5" r="5.2" fill={relleno} />
      </>
    )}
  </Lienzo>
);
